import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';

/**
 * Configuration for table actions
 */
export interface TableActions<T = any> {
  /** Function to handle row editing */
  onEdit?: (row: T, onClose: () => void) => void;
  /** Function to handle row deletion */
  onDelete?: (row: T, onConfirm: () => void) => void;
  /** Function to handle row viewing */
  onView?: (row: T) => void;
  /** Custom action buttons */
  customActions?: Array<{
    key: string;
    label: string;
    icon?: ReactNode;
    onClick: (row: T) => void;
    variant?: 'primary' | 'secondary' | 'danger';
  }>;
}

/**
 * Table-specific configuration
 */
export interface TableConfig {
  /** Table name for identification */
  tableName?: string;
  /** Whether to enable editing */
  enableEditing?: boolean;
  /** Whether to enable deletion */
  enableDelete?: boolean;
  /** Initial editing mode */
  initialEditingMode?: 'cell' | 'row' | 'table' | 'modal';
  /** Table title */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Hook for managing editable table state and operations
 * Provides a clean interface for table operations without coupling to business logic
 */
export function useEditableTable<T = any>(
  data: T[],
  columns: any[],
  config: TableConfig,
  options: {
    onDataUpdate?: (updatedData: T[]) => void;
    onRowUpdate?: (oldRow: T, newRow: T) => Promise<void>;
    onRowCreate?: (newRow: T) => Promise<void>;
    onRowDelete?: (oldRow: T) => Promise<void>;
  } = {}
) {
  const [tableData, setTableData] = useState<T[]>(data);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Modal states - kept in the hook for table operations
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<T | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [rowToEdit, setRowToEdit] = useState<T | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { addToast } = useToast();

  // Sync with external data changes
  const syncData = useCallback((newData: T[]) => {
    setTableData(newData);
    setValidationErrors({});
  }, []);

  // Sync tableData when external data prop changes (e.g., after refetch)
  useEffect(() => {
    setTableData(data);
  }, [data]);

  // Validation helper
  const validateField = useCallback((column: any, value: any) => {
    if (column.validate) {
      return column.validate(value);
    }
    return undefined;
  }, []);

  // Row creation handler
  const handleCreateRow = useCallback(async (values: T) => {
    if (options.onRowCreate) {
      try {
        await options.onRowCreate(values);
      } catch (error) {
        console.error('Failed to create row:', error);
        addToast('Failed to create row', 'error');
        throw error;
      }
    } else {
      // Default behavior: add to local state
      const newData = [...tableData, values];
      setTableData(newData);
      options.onDataUpdate?.(newData);
    }
  }, [tableData, options]);

  // Row deletion handler
  const handleDeleteRow = useCallback((row: T) => {
    setRowToDelete(row);
    setDeleteModalOpen(true);
  }, []);

  // Confirm deletion
  const confirmDelete = useCallback(async () => {
    if (!rowToDelete) return;

    setIsDeleting(true);
    try {
      const rowId = (rowToDelete as any)?.id ?? (rowToDelete as any)?._id;
      
      if (options.onRowDelete) {
        await options.onRowDelete(rowToDelete);
      }
      const newData = rowId !== undefined 
        ? tableData.filter((item) => {
            const itemId = (item as any)?.id ?? (item as any)?._id;
            return itemId !== rowId;
          })
        : tableData.filter((item) => item !== rowToDelete);
      
      setTableData(newData);
      options.onDataUpdate?.(newData);
      
      setDeleteModalOpen(false);
      setRowToDelete(null);
    } catch (error) {
      console.error('Failed to delete row:', error);
      addToast('Failed to delete row', 'error');
    } finally {
      setIsDeleting(false);
    }
  }, [rowToDelete, tableData, options, addToast]);

  // Cancel deletion
  const cancelDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setRowToDelete(null);
  }, []);

  // Row edit handler
  const handleEditRow = useCallback((row: T) => {
    setRowToEdit(row);
    setEditModalOpen(true);
  }, []);

  // Cell save handler
  const handleCellSave = useCallback(
    async (rowIndex: number, columnId: string, value: any, column: any) => {
      // Clear validation error
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[columnId];
        return newErrors;
      });

      // Validate field
      const error = validateField(column, value);
      if (error) {
        setValidationErrors((prev) => ({ ...prev, [columnId]: error }));
        return false;
      }

      // Update local data
      const updatedData = [...tableData];
      const oldRow = updatedData[rowIndex];
      updatedData[rowIndex] = {
        ...updatedData[rowIndex],
        [columnId]: value,
      };

      setTableData(updatedData);
      options.onDataUpdate?.(updatedData);

      // Call async update callback
      if (options.onRowUpdate) {
        try {
          await options.onRowUpdate(oldRow, updatedData[rowIndex]);
          return true;
        } catch (error) {
          console.error('Failed to update row:', error);
          addToast('Failed to update row', 'error');
          // Revert change
          setTableData(tableData);
          return false;
        }
      }

      return true;
    },
    [tableData, validateField, options]
  );

  // Modal save handler
  const handleModalSave = useCallback(async () => {
    if (!rowToEdit || !config.tableName) return;

    // Validate all editable fields
    const newErrors: Record<string, string> = {};
    columns.forEach((column) => {
      if (column.editable && column.validate) {
        const value = (rowToEdit as any)[column.key];
        const error = column.validate(value);
        if (error) {
          newErrors[column.key] = error;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      const rowIndex = tableData.indexOf(rowToEdit);
      const firstEditableColumn = columns.find(col => col.editable && col.key !== 'actions');
      if (firstEditableColumn) {
        await handleCellSave(rowIndex, firstEditableColumn.key, (rowToEdit as any)[firstEditableColumn.key], firstEditableColumn);
      }
      setEditModalOpen(false);
      setRowToEdit(null);
      setValidationErrors({});
    } catch (error) {
      console.error('Failed to save row:', error);
      addToast('Failed to save changes', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [rowToEdit, columns, tableData, handleCellSave, config.tableName]);

  // Close modal handlers
  const closeEditModal = useCallback(() => {
    setEditModalOpen(false);
    setRowToEdit(null);
    setValidationErrors({});
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setRowToDelete(null);
  }, []);

  return {
    // State
    tableData,
    validationErrors,
    deleteModalOpen,
    rowToDelete,
    isDeleting,
    editModalOpen,
    rowToEdit,
    isSaving,

    // Actions
    syncData,
    handleCreateRow,
    handleDeleteRow,
    confirmDelete,
    cancelDelete,
    handleEditRow,
    handleCellSave,
    handleModalSave,
    closeEditModal,
    closeDeleteModal,

    // Utilities
    setValidationErrors,
    validateField,
  };
}